import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, takeUntil, Subscription } from 'rxjs';
import { DataProviderService } from 'src/app/services/data-provider.service';
import { LoadingController, Platform, AlertController } from '@ionic/angular';
import { QuestionnaireSharedService } from 'src/app/services/questionnaire-shared.service';
import { TranslatePipe } from '@ngx-translate/core';

import { BackButtonService } from 'src/app/services/back-button.service';
import { QuestionSaveService } from 'src/app/services/question-save.service';
import { SnackbarService } from '@bcl/design-system';

@Component({
  selector: 'app-questionnaire',
  templateUrl: './questionnaire.page.html',
  styleUrls: ['./questionnaire.page.scss'],
})
export class QuestionnairePage implements OnInit, OnDestroy {
  componentDestroyed$: Subject<boolean> = new Subject();
  id: number;
  parentObject: string;
  customFormId: number;
  routerState: any;
  questionsResponse: any = [];
  questions: any = [];

  questionsAnswered = false;
  allRequiredAnswered = false;
  questionnaireId: number | null = null;
  editable = true;
  private loadingDialog: HTMLIonLoadingElement;
  private backButtonSubscription: Subscription;

  constructor(
    private dataService: DataProviderService,
    private route: ActivatedRoute,
    private router: Router,
    private loadingController: LoadingController,
    private questionnaireSharedService: QuestionnaireSharedService,
    private questionSaveService: QuestionSaveService,
    private alertController: AlertController,
    private translatePipe: TranslatePipe,
    private location: Location,
    private platform: Platform,
    private backButtonService: BackButtonService,
    private snackbarService: SnackbarService
  ) {
    this.id = parseInt(this.route.snapshot.paramMap.get('id'), 10);
    this.parentObject = this.route.snapshot.paramMap.get('parentObject');
    this.customFormId = parseInt(
      this.route.snapshot.paramMap.get('customFormId'),
      10
    );
    this.routerState = this.router.getCurrentNavigation().extras.state;
    this.questionnaireId = this.routerState?.questionnaireId ?? null;
    this.editable = this.routerState?.editable ?? true;

    questionnaireSharedService.onNewPhotosEvent.subscribe((newPhotos) => {
      this.changeQuestionObject(newPhotos);
    });
    questionnaireSharedService.onAnswerChangeEvent.subscribe((answerObject) => {
      this.changeQuestionAnswer(answerObject);
    });
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  ionViewWillEnter(): void {
    this.backButtonService.showBackButton({
      show: true,
      action: () => {
        this.goBack();
      },
    });
  }

  ngOnInit(): void {
    this.backButtonSubscription =
      this.platform.backButton.subscribeWithPriority(10, () => {
        this.goBack();
      });
    this.questions = [];
    this.getQuestionaire();
  }

  async goBack(): Promise<void> {
    this.checkAnswers();
    if (!this.allRequiredAnswered) {
      const alert = await this.alertController.create({
        header: this.translatePipe.transform('QUESTIONNAIRE_PAGE.WARNING'),
        message: this.translatePipe.transform(
          'QUESTIONNAIRE_PAGE.NOT_ALL_REQUIRED_QUESTIONS_ANSWERED'
        ),
        buttons: [
          {
            text: this.translatePipe.transform('QUESTIONNAIRE_PAGE.NO'),
            role: 'cancel',
          },
          {
            text: this.translatePipe.transform('QUESTIONNAIRE_PAGE.YES'),
            handler: () => {
              this.router.navigateByUrl(`${this.routerState?.backUrl}`);
            },
          },
        ],
      });
      await alert.present();
    } else {
      this.router.navigateByUrl(`${this.routerState?.backUrl}`);
    }
  }

  async getQuestionaire() {
    await this.showLoading();
    this.dataService
      .getQuestionnaire(
        this.id,
        this.parentObject,
        this.customFormId,
        true,
        this.questionnaireId
      )
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(
        (result) => {
          this.questionsResponse = result?.questions;
          const categories = new Set();
          let allAnswered = true;

          this.questionsResponse.forEach((question) => {
            if (question.conditionQuestion) {
              const conditionQuestion = this.questionsResponse.find(
                (questionTemp) =>
                  questionTemp.questionId === question.conditionQuestion
              );
              let answerMatch = false;
              if (!question.conditionValue) {
                if (
                  conditionQuestion?.questionAnswer &&
                  conditionQuestion?.questionAnswers !== 'checkbox'
                ) {
                  question.Visible = true;
                } else if (
                  conditionQuestion?.questionAnswer === 'true' &&
                  conditionQuestion?.questionAnswers === 'checkbox'
                ) {
                  question.Visible = true;
                } else {
                  question.Visible = false;
                  question.questionAnswer = '';
                }
              } else {
                const answers = question.conditionValue.split(';');

                answers.forEach((answer) => {
                  let questionArray: any = null;
                  try {
                    questionArray = JSON.parse(
                      conditionQuestion?.questionAnswer
                    );
                  } catch (e) {}
                  if (Array.isArray(questionArray)) {
                    if (questionArray.includes(answer)) {
                      answerMatch = true;
                    }
                  } else {
                    if (conditionQuestion?.questionAnswer === answer) {
                      answerMatch = true;
                    }
                  }
                });
                if (answerMatch) {
                  question.Visible = true;
                } else {
                  question.Visible = false;
                  question.questionAnswer = '';
                }
              }
            } else {
              question.Visible = true;
            }
            if (!categories.has(question.categoryKey)) {
              categories.add(question.categoryKey);
              this.questions.push({
                categoryKey: question.categoryKey,
                categoryName: question.categoryName,
                categorySequence: question.categorySequence,
                questions: [question],
              });
            } else {
              const categoryTemp = this.questions.find(
                (category) => category.categoryKey === question.categoryKey
              );
              categoryTemp.questions.push(question);
            }
            if (
              question.questionAnswers === 'checkbox' &&
              question.questionAnswer === ''
            ) {
              question.questionAnswer = 'false';
            }
            if (
              (!question.questionAnswer ||
                question.questionAnswer === '' ||
                (question.type === 'select_multiple' &&
                  question.questionAnswer === '[]')) &&
              question.Visible
            ) {
              allAnswered = false;
            }
          });

          this.questionsAnswered = allAnswered;
          this.loadingDialog.dismiss();
        },
        (error) => {
          this.loadingDialog.dismiss();
          this.snackbarService.open(
            'red',
            'QUESTIONNAIRE_PAGE.ERROR_LOADING_QUESTIONNAIRE'
          );
          if (this.routerState?.backUrl) {
            this.router.navigateByUrl(this.routerState.backUrl);
          }
        }
      );
  }

  checkAnswers() {
    let allAnswered = true;
    let allRequiredAnswered = true;

    this.questions?.forEach((category) => {
      category.questions?.forEach((question) => {
        if (
          ((!question.questionAnswer &&
            question.questionAnswers !== 'checkbox') ||
            question.questionAnswer === '' ||
            (question.type === 'select_multiple' &&
              question.questionAnswer === '[]') ||
            question.questionAnswer?.length === 0) &&
          question.Visible
        ) {
          if (question.questionObligatory) {
            allRequiredAnswered = false;
          }
          allAnswered = false;
        }
      });
    });
    this.questionsAnswered = allAnswered;
    this.allRequiredAnswered = allRequiredAnswered;
  }

  trackCategory(index, category) {
    return category ? category.categoryKey : undefined;
  }
  async showLoading() {
    this.loadingDialog = await this.loadingController.create({
      message: this.translatePipe.transform('LOADING'),
    });

    this.loadingDialog.present();
  }

  changeQuestionObject(newPhotos) {
    const category = this.questions?.find(
      (categoryTemp) => categoryTemp.categoryKey === newPhotos?.categoryKey
    );
    const question = category.questions?.find(
      (questionTemp) => questionTemp.answerId === newPhotos.answerId
    );
    question.photosCount = newPhotos?.photosCount;

    const questionStorage = this.questionsResponse?.find(
      (questionTemp) => questionTemp.answerId === newPhotos?.answerId
    );
    if (question) {
      question.photosCount = newPhotos?.photosCount;
      this.dataService.saveOfflineQuestionnaire(
        this.questionsResponse,
        this.parentObject,
        this.id
      );
    }
  }

  changeQuestionAnswer(answerObject) {
    const question = this.questionsResponse?.find(
      (questionTemp) => questionTemp.answerId === answerObject?.answerId
    );

    const questionsToUpdate = this.questionsResponse?.filter(
      (questionTemp) =>
        questionTemp.conditionQuestion === answerObject?.questionId
    );
    questionsToUpdate?.forEach((questionTemp) => {
      let answerMatch = false;
      if (questionTemp.conditionValue) {
        const answers = questionTemp.conditionValue.split(';');

        answers.forEach((answer) => {
          if (Array.isArray(answerObject?.answersArray)) {
            if (answerObject?.answersArray.includes(answer)) {
              answerMatch = true;
            }
          } else {
            if (answerObject?.answer === answer) {
              answerMatch = true;
            }
          }
        });
        if (answerMatch) {
          questionTemp.Visible = true;
        } else {
          questionTemp.Visible = false;
          questionTemp.questionAnswer = null;
          let answer = '';
          if (questionTemp.type === 'select_multiple') {
            answer = '[]';
          } else if (questionTemp.questionAnswers === 'checkbox') {
            answer = 'false';
          }

          questionTemp.questionAnswer = answer;

          this.questionSaveService.saveAnswer(
            questionTemp,
            '',
            '',
            '',
            this.parentObject,
            answerObject?.VisitId,
            answer
          );
        }
      } else {
        if (answerObject?.answer) {
          if (
            question.questionAnswers === 'checkbox' &&
            (question.questionAnswer === 'false' ||
              question.questionAnswer === '')
          ) {
            questionTemp.Visible = false;
            questionTemp.questionAnswer = null;
            let answer = '';
            if (questionTemp.type === 'select_multiple') {
              answer = '[]';
            } else if (questionTemp.questionAnswers === 'checkbox') {
              answer = 'false';
            }
            questionTemp.questionAnswer = answer;
            this.questionSaveService.saveAnswer(
              questionTemp,
              '',
              '',
              '',
              this.parentObject,
              answerObject?.VisitId,
              answer
            );
          } else {
            questionTemp.Visible = true;
          }
        } else {
          questionTemp.Visible = false;
          questionTemp.questionAnswer = null;
          let answer = '';
          if (questionTemp.type === 'select_multiple') {
            answer = '[]';
          } else if (questionTemp.questionAnswers === 'checkbox') {
            answer = 'false';
          }

          questionTemp.questionAnswer = answer;
          this.questionSaveService.saveAnswer(
            questionTemp,
            '',
            '',
            '',
            this.parentObject,
            answerObject?.VisitId,
            answer
          );
        }
      }
    });
    if (question) {
      if (question.type === 'select_multiple') {
        if (answerObject?.answer) {
          question.questionAnswer = JSON.parse(answerObject.answer);
        }
      } else {
        question.questionAnswer = answerObject?.answer;
      }

      this.dataService.saveOfflineQuestionnaire(
        this.questionsResponse,
        this.parentObject,
        this.id
      );
    }
    this.checkAnswers();
  }

  ngOnDestroy() {
    this.backButtonSubscription?.unsubscribe();
    this.componentDestroyed$.next(true);
  }
}
